import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@verlo/shared/components/ui/dialog';
import { tablesApi } from '@verlo/shared/services/api';
import { floorTables, isJoined, isOutOfUse } from '../../lib/tonight';

// What a manager can change on the till now the floor is set up in the back office: for
// tonight only, a table out of use, or tables pushed together (backend tonight.py). Both
// end at close. Opened by holding a table, on the cards or the plan.

const REASONS = ['Broken', 'Spill', 'Kept for staff', 'Too cold by the door'];
const free = (t) => String(t.status || '').toLowerCase() === 'available';

const TonightSheet = ({ table, tables, onClose, onChanged }) => {
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [joinWith, setJoinWith] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); setOther(''); setJoinWith([]); }, [table?.id]);
  if (!table) return null;

  // The lead's name says the whole join ("T5 + T6"); the card the manager held may be it.
  const shown = floorTables(tables).find((t) => t.id === (table.joined?.lead || table.id)) || table;
  const occupied = String(table.status || '').toLowerCase() === 'occupied';
  const candidates = tables.filter((t) => t.id !== table.id && free(t) && !isOutOfUse(t) && !isJoined(t))
    .sort((a, b) => Number((b.section || 'Main') === (table.section || 'Main')) - Number((a.section || 'Main') === (table.section || 'Main'))
      || String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  const seats = [table, ...tables.filter((t) => joinWith.includes(t.id))].reduce((n, t) => n + (Number(t.capacity) || 0), 0);

  const run = async (call, done) => {
    setBusy(true);
    try {
      await call();
      toast.success(done);
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change the table");
    } finally {
      setBusy(false);
    }
  };
  const why = reason === 'Other' ? other.trim() : reason;

  return (
    <Dialog open={!!table} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="form" data-testid="tonight-sheet">
        <DialogHeader>
          <DialogTitle>{shown.name} tonight</DialogTitle>
          <DialogDescription>For tonight only: it&apos;s back as set up when the day closes. Tables and seats are set up in the back office.</DialogDescription>
        </DialogHeader>

        {isOutOfUse(table) ? (
          <div className="space-y-3">
            <Notice tone="info" word="Out of use" title={table.out_of_use.reason || 'No reason given'}>
              {table.out_of_use.by_name ? `By ${table.out_of_use.by_name}. ` : ''}Nobody can be seated here until it&apos;s back in use.
            </Notice>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy} data-testid="tonight-back-in-use"
                onClick={() => run(() => tablesApi.backInUse(table.id), `${table.name} is back in use`)}>Back in use</Button>
            </DialogFooter>
          </div>
        ) : isJoined(table) ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-soft">{shown.name} are pushed together for tonight: {shown.capacity} seats, seated as one.</p>
            {occupied && <Notice tone="warn" word="Seated">There&apos;s a party at {shown.name}. Split the tables once they&apos;ve gone.</Notice>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy || occupied} data-testid="tonight-split"
                onClick={() => run(() => tablesApi.split(table.id), `${shown.name} split`)}>Split</Button>
            </DialogFooter>
          </div>
        ) : occupied ? (
          <div className="space-y-3">
            <Notice tone="info" word="Seated">{table.name} has a party on it. Out of use and joining are for a free table.</Notice>
            <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <section className="space-y-2">
              <h3 className="font-heading text-sm font-bold text-ink">Out of use</h3>
              <OptionChips value={reason} onChange={(r) => setReason(reason === r ? '' : r)}
                options={[...REASONS, 'Other'].map((r) => ({ value: r, label: r, testId: `tonight-reason-${r}` }))} />
              {reason === 'Other' && <Input autoFocus maxLength={80} value={other} placeholder="What's wrong with it" onChange={(e) => setOther(e.target.value)} />}
              <Button variant="outline" className="w-full" disabled={busy || !why} data-testid="tonight-out-of-use"
                onClick={() => run(() => tablesApi.outOfUse(table.id, why), `${table.name} is out of use tonight`)}>
                Take {table.name} out of use
              </Button>
            </section>

            <section className="space-y-2 border-t border-hairline pt-4">
              <h3 className="font-heading text-sm font-bold text-ink">Join with another table</h3>
              {candidates.length ? (
                <OptionChips multiple value={joinWith} onChange={setJoinWith}
                  options={candidates.map((t) => ({ value: t.id, label: `${t.name} · ${t.capacity}`, testId: `tonight-join-${t.name}` }))} />
              ) : <p className="text-sm text-ink-faint">No other table is free to join it.</p>}
              <Button className="w-full bg-verlo hover:bg-verlo-hover" disabled={busy || !joinWith.length} data-testid="tonight-join"
                onClick={() => run(() => tablesApi.join(table.id, joinWith),
                  `${[table, ...tables.filter((t) => joinWith.includes(t.id))].map((t) => t.name).join(' + ')} joined: ${seats} seats`)}>
                {joinWith.length ? `Join: ${seats} seats` : 'Choose tables to join'}
              </Button>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TonightSheet;
